// src/lib/glpkAdapter.js
import GLPK from "glpk.js";

let glpkInstance = null;

/**
 * solveWithGLPK(model)
 * model:
 *  - c: number[]               objective coefficients (length = numVars)
 *  - rows: {coeffs:number[], sense:">="|"="|"<=", rhs:number}[]
 *  - lb: number[]              lower bounds per var (length = numVars)
 *  - ub: number[]              upper bounds per var (length = numVars)
 *  - N: number                 number of vars (length of c/lb/ub)
 *  - K: number                 (optional) extra slacks, unused here
 *  - names?: string[]          (optional) variable names; if omitted we use v0..v{N-1}
 */
export async function solveWithGLPK(model) {
  try {
    if (!glpkInstance) {
      glpkInstance = await GLPK();
    }
    const glpk = glpkInstance;

    const { c, rows, lb, ub, N, names } = model;
    const numVars = N ?? (c?.length || 0);
    const varNames = Array.isArray(names) && names.length === numVars
      ? names.slice()
      : Array.from({ length: numVars }, (_, j) => `v${j}`);

    // Build LP using YOUR variable names
    const lp = {
      name: "fundplanner",
      objective: {
        direction: glpk.GLP_MIN,
        name: "obj",
        vars: varNames.map((nm, j) => ({ name: nm, coef: c?.[j] ?? 0 })),
      },
      subjectTo: (rows || []).map((r, i) => ({
        name: `r${i}`,
        vars: varNames.map((nm, j) => ({
          name: nm,
          coef: r.coeffs?.[j] ?? 0, // dense row; 0 if not provided
        })),
        bnds: (() => {
          const sense = r.sense || ">=";
          if (sense === "=") return { type: glpk.GLP_FX, lb: r.rhs ?? 0, ub: r.rhs ?? 0 };
          if (sense === "<=") return { type: glpk.GLP_UP, lb: 0, ub: r.rhs ?? 0 };
          return { type: glpk.GLP_LO, lb: r.rhs ?? 0, ub: 1e50 };
        })(),
      })),
      bounds: varNames.map((nm, j) => ({
        name: nm,
        type: glpk.GLP_DB,
        lb: lb?.[j] ?? 0,
        ub: ub?.[j] ?? 1e50,
      })),
    };

    const res = await glpk.solve(lp, { msglev: glpk.GLP_MSG_ERR });
    const { status, z, vars } = res.result || {};

    if (!vars || ![glpk.GLP_OPT, glpk.GLP_FEAS].includes(status)) {
      return { x: Array(numVars).fill(0), y: [], status, obj: z, vars: {} };
    }

    // Order-preserving arrays if you need them
    const values = varNames.map((nm) => vars[nm] ?? 0);

    return {
      x: values,             // same order as varNames
      y: [],                 // not used here
      status,
      obj: z,
      vars,                  // dictionary keyed by YOUR names (x_0, s_draw_1, ...)
      names: varNames,       // echo back the names for callers if needed
    };
  } catch (err) {
    console.error("GLPK solver failed:", err);
    const numVars = model?.N ?? model?.c?.length ?? 0;
    return { x: Array(numVars).fill(0), y: [], status: null, obj: null, vars: {} };
  }
}
